import React, { useEffect, useRef, useState } from 'react';
import '../../styles/app-ui.scss';

const BURST_COUNT = 150;
const DURATION_MS = 3200;

function readColors() {
  const styles = getComputedStyle(document.documentElement);
  const pick = (name, fallback) => styles.getPropertyValue(name).trim() || fallback;
  return [
    pick('--app-accent', '#dc2626'),
    pick('--app-ok', '#16a34a'),
    pick('--app-warn', '#d97706'),
    pick('--app-confetti-gold', '#eab308'),
  ];
}

function createParticles(width, height, colors) {
  const origins = [
    { x: width * 0.12, bias: -0.4 },
    { x: width * 0.5, bias: 0 },
    { x: width * 0.88, bias: 0.4 },
  ];
  const particles = [];

  origins.forEach((origin) => {
    for (let i = 0; i < BURST_COUNT; i += 1) {
      const spread = (Math.random() - 0.5) * 1.2 + origin.bias;
      const angle = -Math.PI / 2 + spread;
      const speed = 1050 + Math.random() * 750;
      const circle = Math.random() < 0.35;
      particles.push({
        x: origin.x + (Math.random() - 0.5) * 36,
        y: height + 12,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        w: circle ? 7 + Math.random() * 7 : 8 + Math.random() * 9,
        h: circle ? 7 + Math.random() * 7 : 4 + Math.random() * 6,
        rot: Math.random() * Math.PI * 2,
        vr: (Math.random() - 0.5) * 9,
        color: colors[Math.floor(Math.random() * colors.length)],
        circle,
        gravity: 620 + Math.random() * 220,
      });
    }
  });

  return particles;
}

const prefersReducedMotion = () => (
  typeof window !== 'undefined'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches
);

const AppConfetti = () => {
  const canvasRef = useRef(null);
  const [done, setDone] = useState(prefersReducedMotion);

  useEffect(() => {
    if (prefersReducedMotion()) {
      setDone(true);
      return undefined;
    }

    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const context = canvas.getContext('2d');
    let frame = 0;
    let last = performance.now();
    const started = last;
    let particles = [];

    const resize = () => {
      const ratio = window.devicePixelRatio || 1;
      const width = window.innerWidth;
      const height = window.innerHeight;
      canvas.width = Math.floor(width * ratio);
      canvas.height = Math.floor(height * ratio);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      return { width, height };
    };

    const size = resize();
    particles = createParticles(size.width, size.height, readColors());

    const tick = (now) => {
      const dt = Math.min(0.032, (now - last) / 1000);
      last = now;
      const { width, height } = resize();
      const elapsed = now - started;
      context.clearRect(0, 0, width, height);
      context.globalAlpha = elapsed > DURATION_MS - 450
        ? Math.max(0, (DURATION_MS - elapsed) / 450)
        : 1;

      particles.forEach((piece) => {
        piece.vy += piece.gravity * dt;
        piece.x += piece.vx * dt;
        piece.y += piece.vy * dt;
        piece.rot += piece.vr * dt;
        context.save();
        context.translate(piece.x, piece.y);
        context.rotate(piece.rot);
        context.fillStyle = piece.color;
        if (piece.circle) {
          context.beginPath();
          context.arc(0, 0, piece.w / 2, 0, Math.PI * 2);
          context.fill();
        } else {
          context.fillRect(-piece.w / 2, -piece.h / 2, piece.w, piece.h);
        }
        context.restore();
      });

      if (elapsed < DURATION_MS) {
        frame = window.requestAnimationFrame(tick);
      } else {
        setDone(true);
      }
    };

    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, []);

  if (done) return null;

  return <canvas ref={canvasRef} className="app-confetti" aria-hidden="true" />;
};

export default AppConfetti;
